import { bindActionCreators } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';
import { Component, Fragment } from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import { exportExcel } from '~/api/search';
import {
  BULK_COPY_LIMIT,
  BULK_DELETE_LIMIT,
  BULK_EDIT_LIMIT,
  BULK_MOVE_LIMIT,
  SEARCH_RESULTS_LIMIT,
} from '~/common/constants/max_lengths';
import { PAGE_SIZE, PAGE_START } from '~/common/constants/page';
import {
  analyzerBulkCopy,
  analyzerBulkDelete,
  analyzerBulkEdit,
  analyzerBulkEditData,
  analyzerBulkMove,
  getDocument,
} from '~/components/AnalyzerResultsPage/AnalyzerResultsPage.data';
import styles from '~/components/AnalyzerResultsPage/AnalyzerResultsPage.styles';
import { transformSelectedItemsForQueryParam } from '~/components/AnalyzerResultsPage/AnalyzerResultsPage.utils';
import SearchResultsItem from '~/components/AnalyzerResultsPage/SearchResultsItem/SearchResultsItem';
import SearchResultsSidebar from '~/components/AnalyzerResultsPage/SearchResultsSidebar/SearchResultsSidebar';
import DeleteDocumentWithReasonModal from '~/components/Modals/DeleteDocumentWithReasonModal/DeleteDocumentWithReasonModal';
import SaveSearch from '~/components/SearchV2/SaveSearch/SaveSearch';
import { checkSearchQueryHasUnsupportedEntity } from '~/components/SearchV2/utils';
import { trackSegment } from '~/components/SegmentAnalytics';
import EcCheckbox from '~/components/Shared/EcCheckbox/EcCheckbox';
import EcModal from '~/components/Shared/EcModal/EcModal';
import EcPaginate from '~/components/Shared/EcPaginate/EcPaginate';
import EcSelect from '~/components/Shared/EcSelect/EcSelect';
import { showToast } from '~/components/Shared/EcToast/showToast';
import ExportExcelLimitWarning, {
  EXPORT_EXCEL_LIMIT,
} from '~/components/Shared/ExportExcelLimitWarning/ExportExcelLimitWarning';
import withHover from '~/components/Shared/HOCs/withHover/withHover';
import AnalyzerResultsEmptyStateIcon from '~/components/Shared/Icons/AnalyzerResultsEmptyStateIcon';
import BackIcon from '~/components/Shared/Icons/BackIcon';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import SearchLimitWarning from '~/components/Shared/SearchLimitWarning/SearchLimitWarning';
import SearchReIndexInProgressWarning from '~/components/Shared/SearchReIndexInProgressWarning/SearchReIndexInProgressWarning';
import SelectDeselectAllMenu from '~/components/Shared/SelectDeselectAllMenu/SelectDeselectAllMenu';
import { Button } from '~/eds/src/components/Button/Button';
import { ButtonGroup } from '~/eds/src/components/ButtonGroup/ButtonGroup';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { FileMimeType } from '~/enums/FileMimeType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { QueryEntityType } from '~/enums/QueryEntityType';
import { QueryParamType } from '~/enums/QueryParamType';
import { convertQueryV2ToQueryV3 } from '~/features/search/utils/convertQueryV2ToQueryV3';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';
import { testHasFlag } from '~/permissions/utils/testHasFlag';
import { getAlertSections } from '~/redux/api/methods/alert';
import {
  getSearchHandlersByPosition,
  getSearchPositionByHandler,
  testIsSupportedFilterType,
  toFilters,
} from '~/redux/api/methods/search';
import { transformFieldSections } from '~/redux/api/transformers/search';
import { slices } from '~/redux/slices';
import { initContext } from '~/redux/slices/documentsNavigation';
import search from '~/redux/slices/search';
import { withRouting } from '~/routing/withRouting';
import {
  MODAL_DELETE,
  MODAL_DOCUMENT_GROUP,
  MODAL_EXCEL_EXPORT,
  MODAL_FOLDER_TREE,
  MODAL_MULTI_EDIT,
} from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { getUniqueObjectsInArrayByKey } from '~/utils/array';
import { testContainsGhostSearchResult } from '~/utils/document.utils';
import { parseNavigationResponse } from '~/utils/navigation';
import { coercePageIndex, getMaximumByLimit } from '~/utils/number';
import {
  getPageSearchQueryByKey,
  updatePageSearchQuery,
} from '~/utils/searchQuery';
import { featureFlagIncluded } from '~/utils/user';

class Page extends Component {
  constructor(props) {
    super(props);

    this.handleSortChange = this.handleSortChange.bind(this);
    this.handleOnBackClick = this.handleOnBackClick.bind(this);
    this.handleOnPageClick = this.handleOnPageClick.bind(this);
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleHideModal = this.handleHideModal.bind(this);
    this.handleOnSelectPageClick = this.handleOnSelectPageClick.bind(this);
    this.handleOnSelectAllClick = this.handleOnSelectAllClick.bind(this);
    this.handleOnDocumentSelectClick =
      this.handleOnDocumentSelectClick.bind(this);
    this.onBulkMoveCopy = this.onBulkMoveCopy.bind(this);
    this.onBulkDelete = this.onBulkDelete.bind(this);

    this.sortOptions = [
      {
        label: props.intl.formatMessage({
          id: 'AnalyzerResultsPage.sortByNameDesc',
          defaultMessage: 'Sort by: Name ↓',
          description: 'Sort option for name descending',
        }),
        value: 'name-desc',
      },
      {
        label: props.intl.formatMessage({
          id: 'AnalyzerResultsPage.sortByNameAsc',
          defaultMessage: 'Sort by: Name ↑',
          description: 'Sort option for name ascending',
        }),
        value: 'name-asc',
      },
      {
        label: props.intl.formatMessage({
          id: 'AnalyzerResultsPage.sortByUploadDateDesc',
          defaultMessage: 'Sort by: Upload Date ↓',
          description: 'Sort option for upload date descending',
        }),
        value: 'date_added-desc',
      },
      {
        label: props.intl.formatMessage({
          id: 'AnalyzerResultsPage.sortByUploadDateAsc',
          defaultMessage: 'Sort by: Upload Date ↑',
          description: 'Sort option for upload date ascending',
        }),
        value: 'date_added-asc',
      },
      {
        label: props.intl.formatMessage({
          id: 'AnalyzerResultsPage.sortByExpirationDateDesc',
          defaultMessage: 'Sort by: Expiration Date ↓',
          description: 'Sort option for expiration date descending',
        }),
        value: 'ex_date-desc',
      },
      {
        label: props.intl.formatMessage({
          id: 'AnalyzerResultsPage.sortByExpirationDateAsc',
          defaultMessage: 'Sort by: Expiration Date ↑',
          description: 'Sort option for expiration date ascending',
        }),
        value: 'ex_date-asc',
      },
      {
        label: props.intl.formatMessage({
          id: 'AnalyzerResultsPage.sortByEffectiveDateDesc',
          defaultMessage: 'Sort by: Effective Date ↓',
          description: 'Sort option for effective date descending',
        }),
        value: 'effective_date-desc',
      },
      {
        label: props.intl.formatMessage({
          id: 'AnalyzerResultsPage.sortByEffectiveDateAsc',
          defaultMessage: 'Sort by: Effective Date ↑',
          description: 'Sort option for effective date ascending',
        }),
        value: 'effective_date-asc',
      },
      {
        label: props.intl.formatMessage({
          id: 'AnalyzerResultsPage.sortByStartDateDesc',
          defaultMessage: 'Sort by: Start Date ↓',
          description: 'Sort option for start date descending',
        }),
        value: 'start_date-desc',
      },
      {
        label: props.intl.formatMessage({
          id: 'AnalyzerResultsPage.sortByStartDateAsc',
          defaultMessage: 'Sort by: Start Date ↑',
          description: 'Sort option for start date ascending',
        }),
        value: 'start_date-asc',
      },
    ];

    this.state = {
      page: 1,
      loading: true,
      errorLoading: false,
      documents: [],
      resultCount: 0,
      hasAsyncExportInProgress: false,
      exporting: false,
      currentModal: null,
      bulkAction: null,
      expanded: false,
      isPageSelected: false,
      isAllSelected: false,
      searchQuery: [],
      selectedItems: [],
      sortBy: this.sortOptions[0],
    };
  }

  componentDidMount() {
    this.searchWithQuery();
  }

  componentDidUpdate(prevProps) {
    if (this.props.data && prevProps.data !== this.props.data) {
      const { data } = this.props;
      updatePageSearchQuery({
        [QueryParamType.IsAllSelected]: false,
        [QueryParamType.Page]: PAGE_START,
        [QueryParamType.Query]: data,
        [QueryParamType.SelectedItems]: [],
      });
      this.searchWithQuery();
    }
  }

  searchWithQuery() {
    const { currentUser, data, flags } = this.props;
    const expanded =
      getPageSearchQueryByKey(QueryParamType.Expanded, false) === 'true';
    const isAllSelected =
      getPageSearchQueryByKey(QueryParamType.IsAllSelected, false) === 'true';
    const selectedItems = getPageSearchQueryByKey(
      [QueryParamType.SelectedItems],
      [],
    );
    const page = getPageSearchQueryByKey([QueryParamType.Page], PAGE_START);
    const sortedBy = getPageSearchQueryByKey(
      QueryParamType.SortedColumn,
      'name-asc',
    );
    const sortByField =
      this.sortOptions.find((obj) => obj.value === sortedBy) ||
      this.sortOptions[1];
    const queryObject = getPageSearchQueryByKey(QueryParamType.Query, data);

    const hasSearchV2Flag = testHasFlag(FeatureFlagType.SearchV2)(currentUser);
    const shouldRedirectToSearch =
      hasSearchV2Flag && flags[FlagType.SearchPageResultsForAnalyzer];
    if (shouldRedirectToSearch) {
      this.parseQueryToSearchPage(queryObject);
    } else {
      this.setState(
        {
          loading: false,
          expanded,
          isAllSelected,
          page,
          searchQuery: queryObject,
          selectedItems,
          sortBy: sortByField,
        },
        () => {
          this.getSearchResults();
          updatePageSearchQuery({
            [QueryParamType.Expanded]: expanded,
            [QueryParamType.IsAllSelected]: isAllSelected,
            [QueryParamType.Page]: page,
            [QueryParamType.Query]: queryObject,
            [QueryParamType.SelectedItems]: selectedItems,
            [QueryParamType.SortedColumn]: sortByField.value || 'name-asc',
          });
        },
      );
    }
  }

  async parseQueryToSearchPage(queryObject = []) {
    const { dispatch, navigate, intl } = this.props;
    const isComplexSearch = checkSearchQueryHasUnsupportedEntity(queryObject);
    dispatch(search.actions.reset());
    dispatch(slices.searchV3.actions.reset());
    if (isComplexSearch) {
      dispatch(search.actions.setUnsupportedQuery(queryObject));
      dispatch(
        slices.searchV3.actions.setQueryBuilder(
          convertQueryV2ToQueryV3(queryObject),
        ),
      );

      // For some reason, the navigate function isn’t ready when it’s called immediately.
      setTimeout(() => {
        navigate('/search');
      }, 1000);
    } else {
      try {
        const sectionsResponse = await getAlertSections();
        const supportedFieldEntities = queryObject.filter((filter) =>
          testIsSupportedFilterType(filter),
        );
        const filtersSupported = toFilters(supportedFieldEntities);
        const booleanQuery =
          queryObject.find(
            (filter) => filter.entity === QueryEntityType.BoolTextSearch,
          )?.query || '';

        dispatch(
          search.actions.setQuery({
            booleanQuery,
            filters: filtersSupported,
            fields: transformFieldSections(sectionsResponse),
          }),
        );
        dispatch(search.actions.setBooleanQuery(booleanQuery));
        dispatch(search.actions.setFilters(filtersSupported));
        dispatch(search.actions.setPage(1));

        dispatch(slices.searchV3.actions.setSearchText(booleanQuery));
        dispatch(slices.searchV3.actions.setSelectedFilters(filtersSupported));
        dispatch(slices.searchV3.actions.setPage(1));

        navigate('/search');
      } catch {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'AnalyzerResultsPage.searchQueryProcessingError',
            defaultMessage:
              'An error occurred while processing the search query.',
            description: 'Error message when search query processing fails',
          }),
        );
        this.setState({ loading: false });
      }
    }
  }

  componentWillUnmount() {
    const path = this.props.location.pathname;

    if (path !== '/analyzer' && !path.includes('/document/')) {
      this.props.analyzerResetData();
    }
  }

  getSearchResults() {
    this.setState({ loading: true });
    const { page, searchQuery, sortBy } = this.state;

    const requestData = { query: searchQuery };

    getDocument(requestData, sortBy.value, page, PAGE_SIZE)
      .then((res) => {
        this.setState({
          documents: res.results,
          resultCount: res.count,
          totalDocsIncludingDups: res.totalDocsIncludingDups,
          fullyIndexed: res.fullyIndexed,
          docCountIndexing: res.docCountIndexing,
          loading: false,
          errorLoading: false,
          hasAsyncExportInProgress: res.isDuplicateExport,
        });
      })
      .catch(() => this.setState({ loading: false, errorLoading: true }));
  }

  exportFromLastSavedSetting = () => {
    this.onExport(null);
  };

  onExport = (exportSetting) => {
    const { intl, formatDate } = this.props;
    const { searchQuery, sortBy } = this.state;
    const requestData = { query: searchQuery };

    this.setState({ exporting: true });
    trackSegment('Exporting excel', requestData);
    // according to the documentation for getTimezoneOffset(), pt (-7) would be 7 instead
    const offsetFromUTCInHour = -(new Date().getTimezoneOffset() / 60);
    const formData = exportSetting
      ? { ...requestData, sortBy, offsetFromUTCInHour, exportSetting }
      : { ...requestData, sortBy, offsetFromUTCInHour };
    exportExcel(formData)
      .then((res) => {
        let excelMessage = intl.formatMessage({
          id: 'AnalyzerResultsPage.exportStartedMessage',
          defaultMessage: 'Your results have started exporting. ',
          description: 'Message when export has started',
        });
        if (res.status === HttpStatusCodeType.Ok) {
          showToast(SUCCESS, excelMessage);
          const blob = new Blob([res.data], { type: FileMimeType.Xlsx });
          const dateString = formatDate(new Date(), 'utc');
          saveAs(blob, `${dateString} analyzer.xlsx`);
        } else if (res.status === HttpStatusCodeType.Accepted) {
          showToast(
            SUCCESS,
            excelMessage +
              intl.formatMessage({
                id: 'AnalyzerResultsPage.exportEmailNotificationMessage',
                defaultMessage:
                  'You will receive an email when the export is complete.',
                description:
                  'Message about email notification for export completion',
              }),
          );
          this.setState({ hasAsyncExportInProgress: true });
        }
        this.setState({ exporting: false });
        this.handleHideModal();
      })
      .catch(() => {
        showToast(
          ERROR,
          intl.formatMessage({
            id: 'AnalyzerResultsPage.exportError',
            defaultMessage:
              'Error exporting data, contact support@evisort for help.',
            description: 'Error message when export fails',
          }),
        );
        this.setState({ exporting: false });
        this.handleHideModal();
      });
  };

  handleCollapseExpandClick = (expanded) => {
    this.setState({ expanded }, () => {
      updatePageSearchQuery({
        [QueryParamType.Expanded]: expanded,
      });
    });
  };

  handleSortChange(selectedOption) {
    this.setState({ page: PAGE_START, sortBy: selectedOption }, () => {
      updatePageSearchQuery({
        [QueryParamType.Page]: PAGE_START,
        [QueryParamType.SortedColumn]: selectedOption.value,
      });
      this.getSearchResults();
    });
  }

  handleOnPageClick(clickedPage) {
    const { isAllSelected, selectedItems } = this.state;
    const selectedPage = clickedPage.selected + 1;

    this.setState({ page: selectedPage }, () => {
      updatePageSearchQuery(
        {
          [QueryParamType.Page]: selectedPage,
          [QueryParamType.SelectedItems]: selectedItems,
          [QueryParamType.IsAllSelected]: isAllSelected,
        },
        true,
      );
      this.getSearchResults();
    });
  }

  handleShowModal(currentModal, bulkAction = null) {
    this.setState({ currentModal, bulkAction });
  }

  handleHideModal() {
    this.setState({ currentModal: null });
  }

  handleOnBackClick() {
    this.props.navigate(-1);
  }

  updateSelectItemsInUrl(selectedDocs, isAllSelected) {
    updatePageSearchQuery({
      [QueryParamType.IsAllSelected]: isAllSelected,
      [QueryParamType.SelectedItems]: selectedDocs,
    });
  }

  handleOnSelectPageClick(isSelected) {
    const { documents, selectedItems } = this.state;
    const items = isSelected
      ? getUniqueObjectsInArrayByKey([...documents, ...selectedItems], 'id')
      : [];
    const selectedDocs = transformSelectedItemsForQueryParam(items);

    this.setState(
      {
        isPageSelected: isSelected,
        isAllSelected: false,
        selectedItems: selectedDocs,
      },
      () => this.updateSelectItemsInUrl(selectedDocs, false),
    );
  }

  handleOnSelectAllClick(isAllSelected) {
    this.setState({ isAllSelected });
    updatePageSearchQuery({ [QueryParamType.IsAllSelected]: true });
  }

  handleOnDocumentSelectClick(document) {
    const { isPageSelected, documents, selectedItems } = this.state;
    let newIsPageSelected = isPageSelected;
    let newSelectedDocuments = [...selectedItems];

    if (newSelectedDocuments.filter((d) => d.id === document.id).length) {
      newSelectedDocuments = newSelectedDocuments.filter(
        (d) => d.id !== document.id,
      );
      newIsPageSelected = false;
    } else {
      const docObj = {
        document_handlers: document.document_handlers.map((item) => item.id),
        id: document.id,
        is_movable: document.is_movable,
      };
      newSelectedDocuments.push(docObj);
      if (newSelectedDocuments.length === documents.length)
        newIsPageSelected = true;
    }

    this.setState(
      {
        isPageSelected: newIsPageSelected,
        isAllSelected: false,
        selectedItems: newSelectedDocuments,
      },
      () => this.updateSelectItemsInUrl(newSelectedDocuments, false),
    );
  }

  onBulkMoveCopy(location) {
    const { bulkAction, searchQuery } = this.state;
    const [document_ids, select_all] = this.setBulkParams();

    const analyzerBulkAction =
      bulkAction === 'Move' ? analyzerBulkMove : analyzerBulkCopy;

    analyzerBulkAction(location.id, document_ids, searchQuery, select_all)
      .then((res) => showToast(SUCCESS, res.detail))
      .catch((err) =>
        showToast(
          ERROR,
          err?.response?.data?.detail ||
            this.props.intl.formatMessage({
              id: 'AnalyzerResultsPage.bulkActionError',
              defaultMessage: 'An error occurred.',
              description: 'Generic error message for bulk actions',
            }),
        ),
      )
      .finally(() =>
        this.setState({ bulkAction: null }, this.getSearchResults),
      );
  }

  onBulkDelete(reason, extraDetails) {
    const { intl } = this.props;
    const { searchQuery } = this.state;
    const [document_ids, select_all] = this.setBulkParams();

    analyzerBulkDelete(
      document_ids,
      searchQuery,
      select_all,
      reason,
      extraDetails,
    )
      .then((res) => showToast(SUCCESS, res.detail))
      .catch((err) =>
        showToast(
          ERROR,
          err?.response?.data?.detail ||
            intl.formatMessage({
              id: 'AnalyzerResultsPage.bulkDeleteError',
              defaultMessage: 'An error occurred while deleting the documents.',
              description: 'Error message when bulk delete fails',
            }),
        ),
      )
      .finally(() =>
        this.setState({ selectedItems: [] }, this.getSearchResults),
      );
  }

  setBulkParams() {
    const { selectedItems, isAllSelected } = this.state;

    let document_ids = [];
    let select_all = false;

    if (isAllSelected) {
      select_all = true;
    } else {
      selectedItems.forEach((document) =>
        document.document_handlers.forEach((id) => document_ids.push(id)),
      );
    }

    return [document_ids, select_all];
  }

  renderBulkDeleteModalText() {
    const { selectedItems, isAllSelected, resultCount } = this.state;
    const display_count = isAllSelected ? resultCount : selectedItems.length;

    return (
      <FormattedMessage
        id="AnalyzerResultsPage.bulkDeleteConfirmationText"
        defaultMessage="Are you sure you want to delete {count} {count, plural, one {document} other {documents}} you have selected? You won't be able to undo this action."
        description="Confirmation text for bulk delete action. Example: count = '5'"
        values={{
          count: display_count,
        }}
      />
    );
  }

  renderDeleteBulkModal() {
    const { intl } = this.props;
    const { selectedItems, isAllSelected, resultCount, currentModal } =
      this.state;
    const { currentUser } = this.props;
    const documentDeleteReasonRequired =
      !!currentUser?.client_config?.document_delete_reason_required;
    const selectedDocumentsCount = isAllSelected
      ? resultCount
      : selectedItems.length;

    if (documentDeleteReasonRequired) {
      return (
        <DeleteDocumentWithReasonModal
          type="document"
          isVisible={currentModal === MODAL_DELETE}
          count={selectedDocumentsCount}
          hideModal={this.handleHideModal}
          onConfirmDelete={this.onBulkDelete}
        />
      );
    }

    return (
      <EcModal
        modalType={MODAL_DELETE}
        width="560px"
        title={intl.formatMessage(
          {
            id: 'AnalyzerResultsPage.deleteDocumentModalTitle',
            defaultMessage:
              'Delete {count, plural, one {Document} other {Documents}}?',
            description: 'Title for delete document modal. Example: count = 3',
          },
          {
            count: selectedDocumentsCount,
          },
        )}
        text={this.renderBulkDeleteModalText()}
        confirmButtonText={intl.formatMessage({
          id: 'AnalyzerResultsPage.deleteButton',
          defaultMessage: 'Delete',
          description: 'Delete button text',
        })}
        deleteItem={this.onBulkDelete}
        hideModal={this.handleHideModal}
        overLimit={selectedDocumentsCount > BULK_DELETE_LIMIT}
      />
    );
  }

  renderMultiEditBulkModal() {
    const { searchQuery, isAllSelected, resultCount, selectedItems, sortBy } =
      this.state;
    const [document_ids, select_all] = this.setBulkParams();
    const selectedDocumentsCount = isAllSelected
      ? resultCount
      : selectedItems.length;

    return (
      <EcModal
        modalType={MODAL_MULTI_EDIT}
        width="800px"
        hideModal={this.handleHideModal}
        getModalData={() =>
          analyzerBulkEditData(document_ids, searchQuery, select_all)
        }
        overLimit={selectedDocumentsCount > BULK_EDIT_LIMIT}
        update={(fieldsData) =>
          analyzerBulkEdit({
            fields: fieldsData,
            query: isAllSelected ? searchQuery : undefined,
            document_handler_ids: document_ids,
            order_by: sortBy.value,
          })
        }
        onUpdateComplete={() => this.getSearchResults()}
      />
    );
  }

  renderFolderTreeBulkModal() {
    const { intl } = this.props;
    const { bulkAction, isAllSelected, resultCount, selectedItems } =
      this.state;
    const selectedDocumentsCount = isAllSelected
      ? resultCount
      : selectedItems.length;
    const limitCountPerActionType =
      bulkAction === 'Move' ? BULK_MOVE_LIMIT : BULK_COPY_LIMIT;

    return (
      <EcModal
        modalType={MODAL_FOLDER_TREE}
        width="540px"
        title={intl.formatMessage(
          {
            id: 'AnalyzerResultsPage.moveToFolderModalTitle',
            defaultMessage: '{action} to Folder',
            description:
              'Title for move/copy to folder modal. Example: action = "Move", "Copy"',
          },
          {
            action: bulkAction,
          },
        )}
        folderIdsSelected={[]}
        handleUploadLocationChange={this.onBulkMoveCopy}
        confirmButtonText={bulkAction}
        hideModal={this.handleHideModal}
        bulkAction={bulkAction}
        overLimit={selectedDocumentsCount > limitCountPerActionType}
      />
    );
  }

  renderAddToGroupModal() {
    const { intl } = this.props;
    const { selectedItems, searchQuery, isAllSelected } = this.state;

    return (
      <EcModal
        modalType={MODAL_DOCUMENT_GROUP}
        width="660px"
        title={intl.formatMessage({
          id: 'AnalyzerResultsPage.addToGroupModalTitle',
          defaultMessage: 'Add to Group',
          description: 'Title for add to group modal',
        })}
        documentIds={selectedItems.map((doc) => doc.id)}
        searchQuery={isAllSelected ? searchQuery : undefined}
        hideModal={this.handleHideModal}
      />
    );
  }

  renderExportSettingModal() {
    const { intl } = this.props;
    const { resultCount, searchQuery, totalDocsIncludingDups } = this.state;

    return (
      <EcModal
        width="800px"
        modalType={MODAL_EXCEL_EXPORT}
        analyzerSearchQuery={searchQuery}
        title={intl.formatMessage({
          id: 'AnalyzerResultsPage.exportSettingsModalTitle',
          defaultMessage: 'Search Result Excel Export Settings',
          description: 'Title for export settings modal',
        })}
        totalDocs={resultCount}
        totalDups={totalDocsIncludingDups}
        onExport={this.onExport}
        hideModal={this.handleHideModal}
      />
    );
  }

  renderContentPagination() {
    const { classes } = this.props;
    const { page, resultCount } = this.state;

    if (resultCount <= PAGE_SIZE) return null;

    const resultsToShow = getMaximumByLimit(resultCount, SEARCH_RESULTS_LIMIT);

    return (
      <div className={classes.paginationWrapper}>
        <EcPaginate
          onPageChange={this.handleOnPageClick}
          pageCount={Math.ceil(resultsToShow / PAGE_SIZE)}
          forcePage={coercePageIndex(page)}
        />
      </div>
    );
  }

  initializeNavigationContext = ({ id: handlerId }) => {
    const { searchQuery, sortBy } = this.state;
    const { location } = this.props; //ensure this is provided by the React Router's <Route> because it is immutable, unlike window.location or history.location which is mutable
    // search is empty in react router because we are using window.history.replaceState
    // TODO: update react router history in updatePageSearchQuery
    const fromLocation = { ...location, search: window.location.search };

    const navigationAPI = (position) => {
      return getSearchHandlersByPosition({
        query: searchQuery,
        order_by: sortBy.value,
        position,
      });
    };

    const getPosition = (handler_id) => {
      return getSearchPositionByHandler({
        handler_id,
        order_by: sortBy.value,
        query: searchQuery,
      });
    };

    const initOptions = {
      handlerId,
      api: navigationAPI,
      getPosition,
      name: 'search results',
      parseResponse: parseNavigationResponse,
      fromLocation,
    };
    this.props.initContext(initOptions);
  };

  renderContentList() {
    const { currentUser } = this.props;
    const { documents, expanded, isAllSelected, selectedItems } = this.state;

    return documents
      ? documents.map((document) => (
          <SearchResultsItem
            user={currentUser}
            key={document.id}
            document={document}
            showExpanded={expanded}
            isSelected={
              !!selectedItems.filter((d) => d.id === document.id).length ||
              isAllSelected
            }
            onSelect={this.handleOnDocumentSelectClick}
            onNavigate={this.initializeNavigationContext}
          />
        ))
      : null;
  }

  renderSortFilter() {
    const { sortBy } = this.state;

    return (
      <EcSelect
        value={this.sortOptions.find((item) => item.value === sortBy.value)}
        options={this.sortOptions}
        onChange={(newValue) => {
          this.handleSortChange(newValue);
        }}
        width="250px"
        isSearchable={false}
        isClearable={false}
      />
    );
  }

  renderViewFilter() {
    const { intl } = this.props;
    const { expanded } = this.state;

    return (
      <Layout>
        <ButtonGroup
          actions={[
            {
              text: intl.formatMessage({
                id: 'AnalyzerResultsPage.collapseAllButton',
                defaultMessage: 'Collapse All',
                description: 'Button text to collapse all items',
              }),
              level: !expanded ? 'secondary-active' : 'secondary',
              onClick: expanded
                ? () => this.handleCollapseExpandClick(false)
                : null,
            },
            {
              text: intl.formatMessage({
                id: 'AnalyzerResultsPage.expandAllButton',
                defaultMessage: 'Expand All',
                description: 'Button text to expand all items',
              }),
              onClick: expanded
                ? null
                : () => this.handleCollapseExpandClick(true),
              level: expanded ? 'secondary-active' : 'secondary',
            },
          ]}
        />
      </Layout>
    );
  }

  getMovableDocuments = () => {
    const { selectedItems } = this.state;
    return selectedItems.filter((document) => document.is_movable);
  };

  renderHeaderDeleteAction() {
    const { intl } = this.props;
    const { selectedItems } = this.state;

    const movableDocuments = this.getMovableDocuments();
    const disabled = selectedItems.length !== movableDocuments.length;
    const disabledTooltip = disabled
      ? intl.formatMessage({
          id: 'AnalyzerResultsPage.deletePrivilegesError',
          defaultMessage:
            'You do not have privileges to delete one or more of the items you have selected.',
          description: 'Error message when user lacks delete privileges',
        })
      : undefined;

    return (
      <Button
        variant="action"
        text={intl.formatMessage({
          id: 'AnalyzerResultsPage.deleteActionButton',
          defaultMessage: 'Delete',
          description: 'Delete action button text',
        })}
        icon="trash"
        iconPosition="left"
        onClick={() => this.handleShowModal(MODAL_DELETE)}
        id="analyzer-results-bulk-delete"
        aria-haspopup={true}
        disabled={disabled}
        tooltip={disabledTooltip}
      />
    );
  }

  renderHeaderEditAction() {
    const { intl } = this.props;
    const { selectedItems } = this.state;

    const movableDocuments = this.getMovableDocuments();

    return (
      <Button
        id="analyzer-results-bulk-edit"
        onClick={() => this.handleShowModal(MODAL_MULTI_EDIT)}
        text={intl.formatMessage({
          id: 'AnalyzerResultsPage.editActionButton',
          defaultMessage: 'Edit',
          description: 'Edit action button text',
        })}
        icon="edit"
        iconPosition="left"
        tooltip={
          selectedItems.length !== movableDocuments.length
            ? intl.formatMessage({
                id: 'AnalyzerResultsPage.editPrivilegesError',
                defaultMessage:
                  'You do not have privileges to edit one or more of the items you have selected.',
                description: 'Error message when user lacks edit privileges',
              })
            : undefined
        }
        disabled={selectedItems.length !== movableDocuments.length}
        variant="action"
        aria-haspopup={true}
      />
    );
  }

  renderHeaderCopyAction() {
    const { intl } = this.props;
    const { selectedItems } = this.state;

    const movableDocuments = this.getMovableDocuments();

    return (
      <Button
        text={intl.formatMessage({
          id: 'AnalyzerResultsPage.copyActionButton',
          defaultMessage: 'Copy',
          description: 'Copy action button text',
        })}
        icon="clipboard"
        iconPosition="left"
        onClick={() => this.handleShowModal(MODAL_FOLDER_TREE, 'Copy')}
        aria-haspopup={true}
        id="analyzer-results-bulk-copy"
        tooltip={
          selectedItems.length !== movableDocuments.length
            ? intl.formatMessage({
                id: 'AnalyzerResultsPage.copyPrivilegesError',
                defaultMessage:
                  'You do not have privileges to copy one or more of the items you have selected.',
                description: 'Error message when user lacks copy privileges',
              })
            : undefined
        }
        disabled={selectedItems.length !== movableDocuments.length}
        variant="action"
      />
    );
  }

  renderHeaderMoveAction() {
    const { intl } = this.props;
    const { selectedItems } = this.state;

    const movableDocuments = this.getMovableDocuments();

    return (
      <Button
        text={intl.formatMessage({
          id: 'AnalyzerResultsPage.moveActionButton',
          defaultMessage: 'Move',
          description: 'Move action button text',
        })}
        iconPosition="left"
        icon="field-folder"
        tooltip={
          selectedItems.length !== movableDocuments.length
            ? intl.formatMessage({
                id: 'AnalyzerResultsPage.movePrivilegesError',
                defaultMessage:
                  'You do not have privileges to move one or more of the items you have selected.',
                description: 'Error message when user lacks move privileges',
              })
            : undefined
        }
        disabled={selectedItems.length !== movableDocuments.length}
        onClick={() => this.handleShowModal(MODAL_FOLDER_TREE, 'Move')}
        id="analyzer-results-bulk-move"
        variant="action"
      />
    );
  }

  renderAddToGroupAction() {
    const { intl } = this.props;
    const {
      selectedItems = [],
      onClick = () => this.handleShowModal(MODAL_DOCUMENT_GROUP),
    } = this.state;
    const shouldDisable = testContainsGhostSearchResult(selectedItems);

    return (
      <Button
        disabled={shouldDisable}
        tooltip={
          shouldDisable
            ? intl.formatMessage({
                id: 'AnalyzerResultsPage.addToGroupPrivilegesError',
                defaultMessage:
                  'You do not have privileges to add one or more of the items you have selected to a group.',
                description:
                  'Error message when user lacks add to group privileges',
              })
            : undefined
        }
        id="analyzer-results-bulk-add-to-group"
        onClick={onClick}
        text={intl.formatMessage({
          id: 'AnalyzerResultsPage.addToGroupActionButton',
          defaultMessage: 'Add to Group',
          description: 'Add to group action button text',
        })}
        iconPosition="left"
        icon="groups"
        variant="action"
        aria-haspopup={true}
      />
    );
  }

  renderContentActions() {
    const { classes, intl } = this.props;
    const {
      isAllSelected,
      isPageSelected,
      documents,
      selectedItems,
      resultCount,
    } = this.state;

    const selectedDocCount = isAllSelected
      ? resultCount
      : selectedItems?.length;

    return (
      <div className={classes.contentActions}>
        <div className={classes.selectContainer}>
          <div
            id="analyzer-results-select-all-checkbox"
            className={classes.checkboxContainer}
          >
            <EcCheckbox
              checked={isAllSelected || isPageSelected}
              id="analyzerResultsSelectAllCheckboxContainer"
              partiallyChecked={
                selectedItems.length > 0 &&
                selectedItems.length < documents.length
              }
              onClick={() => this.handleOnSelectPageClick(!isPageSelected)}
            />
            <label
              className="screenReaderText"
              htmlFor="analyzerResultsSelectAllCheckboxContainer"
            >
              {intl.formatMessage(
                {
                  id: 'AnalyzerResultsPage.selectAllDocumentsLabel',
                  defaultMessage:
                    'select all {count} {count, plural, one {document} other {documents}}',
                  description:
                    'Screen reader label for select all checkbox. Example: count = "25"',
                },
                {
                  count: documents.length,
                },
              )}
            </label>
          </div>
          <SelectDeselectAllMenu
            name={intl.formatMessage({
              id: 'AnalyzerResultsPage.searchResultsMenuName',
              defaultMessage: 'search results',
              description: 'Name for select/deselect all menu',
            })}
            onChange={this.handleOnSelectPageClick}
          />
          <div className={classes.documentCount}>
            {selectedDocCount.length || selectedItems.length ? (
              <Fragment>
                <Fragment>
                  {intl.formatMessage(
                    {
                      id: 'AnalyzerResultsPage.documentsSelectedMessage',
                      defaultMessage:
                        '{count} {count, plural, one {document} other {documents}} selected',
                      description:
                        'Message showing number of selected documents. Example: count = 3',
                    },
                    {
                      count: selectedDocCount,
                    },
                  )}
                </Fragment>
                {isPageSelected &&
                  !isAllSelected &&
                  resultCount > PAGE_SIZE && (
                    <button
                      className={classes.selectAllContent}
                      onClick={() => this.handleOnSelectAllClick(true)}
                    >
                      {intl.formatMessage(
                        {
                          id: 'AnalyzerResultsPage.selectAllResultsButton',
                          defaultMessage:
                            'Select all {count} {count, plural, one {document} other {documents}}',
                          description:
                            'Button text to select all search results. Example: count = "150"',
                        },
                        {
                          count: resultCount,
                        },
                      )}
                    </button>
                  )}
              </Fragment>
            ) : (
              <Fragment>
                {intl.formatMessage(
                  {
                    id: 'AnalyzerResultsPage.documentsFoundMessage',
                    defaultMessage:
                      '{count} {count, plural, one {document} other {documents}} found',
                    description:
                      'Message showing number of documents found. Example: count = 25',
                  },
                  {
                    count: resultCount,
                  },
                )}
              </Fragment>
            )}
          </div>
        </div>
        {!selectedItems.length ? (
          <div className={classes.bulkActionsContainer}>
            {this.renderAddToGroupAction()}
            {this.renderHeaderMoveAction()}
            {this.renderHeaderCopyAction()}
            {this.renderHeaderEditAction()}
            {this.renderHeaderDeleteAction()}
          </div>
        ) : (
          <div className={classes.filterContainer}>
            {this.renderViewFilter()}
            {this.renderSortFilter()}
          </div>
        )}
      </div>
    );
  }

  renderSearchLimitWarning = () => {
    const { resultCount } = this.state;
    return <SearchLimitWarning resultCount={resultCount} />;
  };

  renderSearchReIndexInProgressWarning = () => {
    const { fullyIndexed, docCountIndexing } = this.state;
    return (
      <SearchReIndexInProgressWarning
        fullyIndexed={fullyIndexed}
        docCountIndexing={docCountIndexing}
      />
    );
  };

  renderExportExcelLimitWarning = () => {
    const { resultCount } = this.state;
    return <ExportExcelLimitWarning resultCount={resultCount} />;
  };

  renderEmptyContent() {
    const { classes, intl } = this.props;
    const { errorLoading } = this.state;

    if (errorLoading) return null;

    return (
      <div className={classes.emptyResultsList}>
        <AnalyzerResultsEmptyStateIcon />
        <span className={classes.emptyResultsListTitle}>
          {intl.formatMessage({
            id: 'AnalyzerResultsPage.noDocumentsFoundTitle',
            defaultMessage: 'No documents found.',
            description: 'Title for empty search results',
          })}
        </span>
        <span className={classes.emptyResultsListSubtitle}>
          {intl.formatMessage({
            id: 'AnalyzerResultsPage.noDocumentsFoundSubtitle',
            defaultMessage: 'Try expanding your search criteria.',
            description: 'Subtitle for empty search results',
          })}
        </span>
      </div>
    );
  }

  renderResultsContent() {
    const { classes } = this.props;
    const { loading, resultCount } = this.state;

    return loading ? (
      <div className={classes.loadingContainer}>
        <LoadingSpinner size="medium" />
      </div>
    ) : (
      <div className={classes.content}>
        {this.renderSearchReIndexInProgressWarning()}
        {resultCount === 0 ? (
          this.renderEmptyContent()
        ) : (
          <Fragment>
            {this.renderContentActions()}
            {this.renderSearchLimitWarning()}
            {this.renderExportExcelLimitWarning()}
            {this.renderContentList()}
            {this.renderContentPagination()}
          </Fragment>
        )}
      </div>
    );
  }

  renderResultsSidebar() {
    const { classes } = this.props;
    const { searchQuery } = this.state;

    return (
      <div className={classes.sidebar}>
        <SearchResultsSidebar data={searchQuery} />
      </div>
    );
  }

  renderPageContent() {
    const { classes, intl } = this.props;
    const { errorLoading } = this.state;

    if (errorLoading) {
      return (
        <div className={classes.errorLoadingMessage}>
          {intl.formatMessage({
            id: 'AnalyzerResultsPage.pageLoadingError',
            defaultMessage: 'An error occurred while loading this page.',
            description: 'Error message when page fails to load',
          })}
        </div>
      );
    }

    return (
      <div className={classes.contentContainer}>
        {this.renderResultsSidebar()}
        {this.renderResultsContent()}
      </div>
    );
  }

  renderLoadingState() {
    const { classes } = this.props;

    return (
      <div className={classes.loadingContainer}>
        <LoadingSpinner size="medium" />
      </div>
    );
  }

  renderPageHeader() {
    const { classes, currentUser, intl } = this.props;
    const { documents, exporting, hasAsyncExportInProgress, resultCount } =
      this.state;

    const BackIconWithHover = withHover(BackIcon);
    const hasExportInProgress = exporting || hasAsyncExportInProgress;
    const disableExport =
      resultCount > EXPORT_EXCEL_LIMIT ||
      !documents.length ||
      hasExportInProgress;
    let defaultExportMessage = intl.formatMessage({
      id: 'AnalyzerResultsPage.exportInProgressMessage',
      defaultMessage:
        'This export is already processing. It may take a few minutes.',
      description: 'Message when export is in progress',
    });

    return (
      <div className={classes.headerContainer}>
        <button
          className={classes.headerBackAction}
          title={intl.formatMessage({
            id: 'AnalyzerResultsPage.backToAnalyzerTitle',
            defaultMessage: 'Go back to Analyzer page',
            description: 'Title for back button',
          })}
          onClick={this.handleOnBackClick}
        >
          <Fragment>
            <BackIconWithHover />
          </Fragment>
        </button>
        <h2 className={classes.pageName}>
          {intl.formatMessage({
            id: 'AnalyzerResultsPage.pageTitle',
            defaultMessage: 'Search Results',
            description: 'Page title for search results',
          })}
        </h2>
        <div className={classes.headerActions}>
          <SaveSearch queryProvided={this.state.searchQuery} />
          {featureFlagIncluded(currentUser, 'EXPORT_EXCEL') && (
            // TODO EKP-2727: update ButtonGroup to support this
            <span className={classes.exportButtonGroup}>
              <Button
                tooltip={
                  hasExportInProgress
                    ? hasAsyncExportInProgress
                      ? defaultExportMessage +
                        ' ' +
                        intl.formatMessage({
                          id: 'AnalyzerResultsPage.exportEmailNotificationMessageInProgress',
                          defaultMessage:
                            'You will receive an email when the export is complete.',
                          description:
                            'Message about email notification for export completion',
                        })
                      : defaultExportMessage
                    : undefined
                }
                onClick={this.exportFromLastSavedSetting}
                disabled={disableExport}
                text={
                  hasExportInProgress
                    ? intl.formatMessage({
                        id: 'AnalyzerResultsPage.exportingButton',
                        defaultMessage: 'Exporting...',
                        description: 'Button text when export is in progress',
                      })
                    : intl.formatMessage({
                        id: 'AnalyzerResultsPage.exportExcelButton',
                        defaultMessage: 'Export Excel',
                        description: 'Button text for exporting to Excel',
                      })
                }
                variant="tertiary"
              />
              <IconButton
                variant="tertiary"
                icon="filter"
                text={intl.formatMessage({
                  id: 'AnalyzerResultsPage.exportFilteredResultsButton',
                  defaultMessage: 'Export filtered results',
                  description: 'Button text for exporting filtered results',
                })}
                disabled={disableExport}
                onClick={() => this.handleShowModal(MODAL_EXCEL_EXPORT)}
              />
            </span>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { loading, currentModal } = this.state;

    return (
      <div className="analyzerResultsPage">
        {this.renderPageHeader()}
        {loading ? this.renderLoadingState() : this.renderPageContent()}
        {currentModal === MODAL_DOCUMENT_GROUP && this.renderAddToGroupModal()}
        {currentModal === MODAL_FOLDER_TREE && this.renderFolderTreeBulkModal()}
        {currentModal === MODAL_MULTI_EDIT && this.renderMultiEditBulkModal()}
        {currentModal === MODAL_DELETE && this.renderDeleteBulkModal()}
        {currentModal === MODAL_EXCEL_EXPORT && this.renderExportSettingModal()}
      </div>
    );
  }
}

const mapStateToProps = ({ analyzer, currentUser }) => {
  return { data: analyzer.filters, currentUser };
};

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    ...bindActionCreators(
      { analyzerResetData: slices.analyzer.actions.resetData, initContext },
      dispatch,
    ),
  };
}

Page = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouting(injectIntl(Page)));

export default injectSheet(styles)(injectIntl(withFlags(withFormatDate(Page))));
